import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { dropdownStyles } from '@/lib/dropdownStyles';
import { cn } from '@/lib/utils';
import { isDevelopment } from '@/utils/environment';
import { useLogging } from '@/contexts/LoggingContext';
import { useDevFlags, DEV_FLAGS } from '@/contexts/DevFlagsContext';
import { adminUsersUrl, adminAIPersonasUrl, adminInvitesUrl } from '@/utils/urls';

export interface DevToolsMenuProps {
  shouldShowAdmin: boolean;
  isAdmin: boolean;
  isDevelopmentEnv: boolean;
  className?: string;
}

/**
 * DevToolsMenu - Development tools dropdown menu
 *
 * Features:
 * - Dev Flags submenu with all runtime dev flags
 * - Navigation to test pages
 * - Admin tools section (when user has admin access)
 * - Visual badge indicating admin level
 * - Only shown in development environment
 *
 * Usage:
 *   {isDevelopment() && (
 *     <DevToolsMenu
 *       shouldShowAdmin={shouldShowAdmin}
 *       isAdmin={isAdmin}
 *       isDevelopmentEnv={isDevelopment()}
 *     />
 *   )}
 */
export function DevToolsMenu({ shouldShowAdmin, isAdmin, isDevelopmentEnv, className }: DevToolsMenuProps) {
  const navigate = useNavigate();
  const { config, updateConfig } = useLogging();
  const devFlags = useDevFlags();

  // Determine badge style
  const getBadgeInfo = () => {
    if (!shouldShowAdmin) return null;
    // Red badge ONLY for dev-mode admins (not actual admins)
    if (isDevelopmentEnv && !isAdmin) {
      return {
        text: 'DEV',
        className: 'mr-2 px-1.5 py-0.5 text-xs font-semibold rounded bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
      };
    }
    // Amber badge for actual admins
    return {
      text: 'Admin',
      className: 'mr-2 px-1.5 py-0.5 text-xs font-semibold rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400'
    };
  };

  const badgeInfo = getBadgeInfo();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className={cn(
            'px-2 py-1 text-sm font-medium text-foreground hover:text-primary transition-colors rounded focus:outline-none focus:ring-2 focus:ring-primary inline-flex items-center',
            className
          )}
        >
          {badgeInfo && (
            <span className={badgeInfo.className}>{badgeInfo.text}</span>
          )}
          <span>🛠️ Dev Tools</span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className={dropdownStyles.content} sideOffset={5} align="end">
          {/* Dev Flags Submenu */}
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className={dropdownStyles.item}>
              <span className="flex items-center justify-between w-full">
                <span>🚩 Dev Flags</span>
                <svg
                  className="ml-auto h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent className={dropdownStyles.content} sideOffset={2} alignOffset={-5}>
                {DEV_FLAGS.map((flag) => (
                  <DropdownMenu.Item
                    key={flag.key}
                    onSelect={(e) => {
                      e.preventDefault();
                      devFlags?.toggleFlag(flag.key);
                    }}
                    className={dropdownStyles.item}
                  >
                    <span className="flex items-center justify-between w-full">
                      <span>{flag.emoji} {flag.label}</span>
                      <span
                        className={cn(
                          'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                          devFlags?.flags[flag.key] ? 'bg-primary' : 'bg-gray-600'
                        )}
                      >
                        <span
                          className={cn(
                            'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                            devFlags?.flags[flag.key] ? 'translate-x-[18px]' : 'translate-x-1'
                          )}
                        />
                      </span>
                    </span>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>

          <DropdownMenu.Separator className={dropdownStyles.separator} />

          {/* Tools Section */}
          <DropdownMenu.Label className={dropdownStyles.label}>Tools</DropdownMenu.Label>

          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate('/dev/tool/photos')}
          >
            📸 Photo Inspector
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate('/dev/tools/logging')}
          >
            📊 Logging Configuration
          </DropdownMenu.Item>

          {/* Dozzle - Docker log viewer (development only) */}
          {isDevelopment() && (
            <DropdownMenu.Item
              className={dropdownStyles.item}
              onSelect={() => {
                const dozzlePort = import.meta.env.VITE_DOZZLE_PORT || '8080';
                window.open(`http://localhost:${dozzlePort}`, '_blank');
              }}
            >
              🪵 Docker Logs (Dozzle)
            </DropdownMenu.Item>
          )}

          {/* Clear Auth - clears HttpOnly cookie and localStorage, redirects to login */}
          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => {
              // Clear localStorage auth state
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              // Navigate to server endpoint to clear HttpOnly cookie and redirect to login
              window.location.href = '/api/auth/clear';
            }}
          >
            🧹 Clear All Auth
          </DropdownMenu.Item>

          <DropdownMenu.Separator className={dropdownStyles.separator} />

          {/* Admin Tools Section - Conditional on admin access */}
          {shouldShowAdmin && (
            <>
              <DropdownMenu.Label className={dropdownStyles.label}>Admin Tools</DropdownMenu.Label>

              <DropdownMenu.Item
                className={dropdownStyles.item}
                onSelect={() => navigate('/admin/health')}
              >
                💚 Health Dashboard
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className={dropdownStyles.item}
                onSelect={() => navigate('/admin/obj')}
              >
                🔍 Object Inspector
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className={dropdownStyles.item}
                onSelect={() => navigate(adminUsersUrl())}
              >
                👥 User Management
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className={dropdownStyles.item}
                onSelect={() => navigate('/admin/query-console')}
              >
                🗄️ SQL Query Console
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className={dropdownStyles.item}
                onSelect={() => navigate(adminAIPersonasUrl())}
              >
                🤖 AI Personas
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className={dropdownStyles.item}
                onSelect={() => navigate(adminInvitesUrl())}
              >
                ✉️ Invite Management
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className={dropdownStyles.item}
                onSelect={() => navigate('/admin/job-queue')}
              >
                📋 Job Queue Inspector
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onSelect={(e) => {
                  e.preventDefault();
                  updateConfig({ streamingEnabled: !config.streamingEnabled });
                }}
                className={dropdownStyles.item}
              >
                <span className="flex items-center justify-between w-full">
                  <span>📡 Backend Log Streaming</span>
                  <span
                    className={cn(
                      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                      config.streamingEnabled ? 'bg-primary' : 'bg-gray-600'
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                        config.streamingEnabled ? 'translate-x-[18px]' : 'translate-x-1'
                      )}
                    />
                  </span>
                </span>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className={dropdownStyles.separator} />
            </>
          )}

          {/* Test Pages Section */}
          <DropdownMenu.Label className={dropdownStyles.label}>Test Pages</DropdownMenu.Label>

          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate('/dev/test/photo-collection2')}
          >
            Photo Components Test Page
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate('/dev/test/markup')}
          >
            Markup Annotations Test Page
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className={dropdownStyles.item}
            onSelect={() => navigate('/dev/test/notifications')}
          >
            Notification Testing
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
